import React from 'react';
import logger from '../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });
    
    // Enhanced logging for debugging
    logger.group(`🚨 Error Boundary Caught Error [${errorId}]`);
    logger.error('Error:', error);
    logger.error('Error Info:', errorInfo);
    logger.error('Component Stack:', errorInfo?.componentStack);
    logger.error('Timestamp:', new Date().toISOString());
    if (this.props.componentName) {
      logger.error('Component Name:', this.props.componentName);
    }
    logger.groupEnd();
    
    // Log to localStorage for debugging history
    try {
      const errorLog = JSON.parse(localStorage.getItem('errorLog') || '[]');
      errorLog.push({
        id: errorId,
        timestamp: new Date().toISOString(),
        error: error.toString(),
        componentStack: errorInfo?.componentStack,
        componentName: this.props.componentName || 'Unknown'
      });
      // Keep only last 10 errors
      if (errorLog.length > 10) errorLog.shift();
      localStorage.setItem('errorLog', JSON.stringify(errorLog));
    } catch (e) {
      logger.error('Failed to log error to localStorage:', e);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI from props
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          errorId: this.state.errorId,
          resetError: this.handleReset
        });
      }

      // Default fallback UI with dark mode support
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6">
            {/* Error Icon and Title */}
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Oops! Something went wrong
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We encountered an unexpected error. Don't worry, your data is safe.
                </p>
              </div>
            </div>
            
            {/* Error Message */}
            {this.state.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                  {this.state.error.message || this.state.error.toString()}
                </p>
              </div>
            )}

            {/* Error ID for support */}
            {this.state.errorId && (
              <div className="mb-4 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                <span className="text-gray-600 dark:text-gray-400">Error ID:</span>{' '}
                <code className="text-gray-800 dark:text-gray-200 font-mono">{this.state.errorId}</code>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-emerald-600 dark:bg-emerald-700 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 bg-gray-600 dark:bg-gray-700 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-gray-700 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go Home
              </button>
            </div>

            {/* Try Again Button */}
            <button
              onClick={this.handleReset}
              className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Try Again Without Refresh
            </button>
            
            {/* Development Mode: Error Details */}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <summary className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-800 dark:hover:text-gray-200 font-medium">
                  🔍 Error Details (Development Only)
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Error Message:</p>
                    <pre className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-2 rounded overflow-auto max-h-24 border border-gray-200 dark:border-gray-700">
                      {this.state.error && this.state.error.toString()}
                    </pre>
                  </div>
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Component Stack:</p>
                      <pre className="text-xs bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-2 rounded overflow-auto max-h-32 border border-gray-200 dark:border-gray-700">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Help Text */}
            <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-500">
              If this problem persists, please contact support with the error ID above.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;